/**
 * Which client story, if any, belongs beside a blog post.
 *
 * 565 posts linked to a case study 0 times. The blog argued about revenue from
 * search without once pointing at the revenue we'd produced. This picks the
 * story closest to what the post is about — its industry first, then its
 * topic, then its category — and the post renders it as a short proof block
 * before the FAQ (BlogPost.tsx).
 *
 * The block only ever shows the case study's own figures from caseStudyData.ts,
 * so nothing is paraphrased into a claim the study doesn't make.
 *
 * Pure data and string matching, so the whole mapping is testable and a dry
 * run over every post is one function call.
 */
import { CASE_STUDIES, type CaseStudy, type CaseStudyMetric } from "./caseStudyData.js";

/**
 * Stories never linked from the blog. b2b-saas-platform was added by an AI
 * commit and still carries a "[Client] vs Asana" placeholder; it stays off the
 * blog until someone confirms it is a real engagement.
 */
export const UNLINKED_CASE_STUDIES = new Set(["b2b-saas-platform"]);

type Topic = "local" | "links" | "technical" | "programmatic" | "content" | "ai" | "roi";

const TOPIC_RES: Record<Topic, RegExp> = {
  local: /\blocal\b|google business profile|\bgbp\b|map pack|google maps|citation|\breviews?\b|near me|service area|multi-location|location page/i,
  links: /\blinks?\b|backlink|link building|domain rating|\bdr\b|domain authority|outreach|digital pr|guest post|placements?/i,
  technical: /technical|crawl|\bindex(ing|ed|ation)?\b|sitemap|canonical|redirect|migration|javascript|core web vitals|page speed|schema|site architecture|faceted/i,
  programmatic: /programmatic|\bpseo\b|at scale|templated? pages/i,
  content: /\bcontent\b|\bblog\b|topical|keyword research|copywriting|\bcluster/i,
  ai: /\bai\b|\baeo\b|\bgeo\b|generative|\bllms?\b|chatgpt|perplexity|gemini|ai overviews?|answer engine|\bclaude\b/i,
  roi: /\broi\b|budget|\bcosts?\b|revenue|pipeline|\bboard\b|leadership|\bcfo\b|investment|worth it|retainer|hire an? (seo )?agency|in-house/i,
};

/** Stories that best show each topic, strongest first. */
const TOPIC_STORIES: Record<Topic, string[]> = {
  local: ["minor-surgery-clinic", "solar-installer", "branding-agency"],
  links: ["ai-visa-planning-platform", "invoice-management-saas", "solar-installer"],
  technical: ["private-equity-saas", "healthcare-clinic", "storage-company"],
  programmatic: ["storage-company", "private-equity-saas"],
  content: ["invoice-management-saas", "healthcare-clinic", "minor-surgery-clinic"],
  ai: ["solar-installer"],
  roi: ["branding-agency", "healthcare-clinic", "solar-installer", "invoice-management-saas"],
};

/** An industry named in the title outranks any topic. */
const INDUSTRY_STORIES: Array<{ re: RegExp; stories: string[] }> = [
  { re: /healthcare|\bclinics?\b|medical|patients?|dental|dentists?|doctors?|surgery|aesthetic/i, stories: ["minor-surgery-clinic", "healthcare-clinic"] },
  { re: /fintech|financial services|\bfinance\b|invoic/i, stories: ["invoice-management-saas", "private-equity-saas"] },
  { re: /\bsaas\b|software compan|b2b software/i, stories: ["invoice-management-saas", "private-equity-saas"] },
  { re: /\bsolar\b|renewable|energy|home services?|contractors?|installers?/i, stories: ["solar-installer"] },
  { re: /storage|real estate|property/i, stories: ["storage-company"] },
  { re: /branding|design (studio|agency)|creative agenc/i, stories: ["branding-agency"] },
  { re: /immigration|visa/i, stories: ["ai-visa-planning-platform"] },
];

const CATEGORY_TOPIC: Record<string, Topic> = {
  "seo-strategy": "roi",
  "ai-seo": "ai",
  "claude-seo": "ai",
  "technical-seo": "technical",
  "local-seo": "local",
  "link-building": "links",
  "content-seo": "content",
  "website-authority": "links",
  "programmatic-seo": "programmatic",
};

/**
 * Posts about third-party tools ("Ahrefs vs Semrush", "Best Rank Trackers")
 * get no story: a client's revenue says nothing about which tool to buy.
 */
const TOOL_POST_RE = /\b(tools?|software|platforms?|plugins?|apps?|checkers?|trackers?|extensions?)\b|\bvs\.?\b.*:\s*(pricing|workflow)/i;
const AGENCY_DECISION_RE = /agenc(y|ies)|in-house|freelancer|consultant|outsourc/i;

export interface CaseStudyMatch {
  study: CaseStudy;
  /** The two metrics most relevant to the post's topic. */
  metrics: CaseStudyMetric[];
  /** Why this story was picked — for the dry-run report. */
  reason: string;
}

/** Which topic a metric evidences, read from its label. */
function metricTopic(label: string): Topic | "organic" | "conversion" {
  if (/domain rating|referring|backlink/i.test(label)) return "links";
  if (/revenue|pipeline|return|roi/i.test(label)) return "roi";
  if (/leads|bookings|demo/i.test(label)) return "conversion";
  if (/indexed|site health/i.test(label)) return "technical";
  return "organic";
}

function pickMetrics(study: CaseStudy, topic: Topic | undefined): CaseStudyMetric[] {
  const wanted: Record<Topic, string[]> = {
    links: ["links", "organic"],
    roi: ["roi", "conversion"],
    local: ["conversion", "organic", "roi"],
    technical: ["technical", "organic", "roi"],
    programmatic: ["organic", "technical", "roi"],
    content: ["organic", "roi"],
    ai: ["roi", "organic"],
  };
  const order = topic ? wanted[topic] : [];
  const ranked = [...study.metrics].sort((a, b) => {
    const ia = order.indexOf(metricTopic(a.label));
    const ib = order.indexOf(metricTopic(b.label));
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  return ranked.slice(0, 2);
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/**
 * The best story for a post, or null when none fits (tool posts, or nothing
 * scores). Among equally good stories the slug picks one, so posts on the same
 * topic spread across the stories that fit rather than all citing one.
 */
export function matchCaseStudy(post: { slug: string; title: string; categories: string[] }, studies: CaseStudy[] = CASE_STUDIES): CaseStudyMatch | null {
  const title = post.title;
  if (TOOL_POST_RE.test(title) && !AGENCY_DECISION_RE.test(title)) return null;

  const available = new Map(studies.filter(s => !UNLINKED_CASE_STUDIES.has(s.slug)).map(s => [s.slug, s]));
  const scores = new Map<string, { score: number; why: string[] }>();
  const add = (slug: string, pts: number, why: string) => {
    if (!available.has(slug)) return;
    const cur = scores.get(slug) ?? { score: 0, why: [] };
    cur.score += pts;
    cur.why.push(why);
    scores.set(slug, cur);
  };

  for (const { re, stories } of INDUSTRY_STORIES) {
    if (re.test(title)) stories.forEach((s, i) => add(s, 6 - i, `industry "${title.match(re)?.[0]}"`));
  }

  let primaryTopic: Topic | undefined;
  for (const topic of Object.keys(TOPIC_RES) as Topic[]) {
    if (TOPIC_RES[topic].test(title)) {
      primaryTopic ??= topic;
      TOPIC_STORIES[topic].forEach((s, i) => add(s, 3 - Math.min(i, 2) * 0.5, `topic ${topic}`));
    }
  }

  const catTopic = post.categories.map(c => CATEGORY_TOPIC[c]).find(Boolean);
  if (catTopic) {
    primaryTopic ??= catTopic;
    TOPIC_STORIES[catTopic].forEach((s, i) => add(s, 2 - Math.min(i, 2) * 0.5, `category ${catTopic}`));
  }

  if (scores.size === 0) return null;
  const best = Math.max(...Array.from(scores.values()).map(v => v.score));
  // Stories within a point of the best share the posts, so a topic's 50 posts
  // don't all cite the same client.
  const top = Array.from(scores.entries()).filter(([, v]) => v.score >= best - 1).map(([k]) => k).sort();
  const slug = top[hash(post.slug) % top.length];
  const study = available.get(slug)!;
  return {
    study,
    metrics: pickMetrics(study, primaryTopic),
    reason: Array.from(new Set(scores.get(slug)!.why)).join(", "),
  };
}
